import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Intent } from '@blueprintjs/core';

import { FormCheckboxList, FormDateRangeInput, FormLocationSelector } from 'components/form-fields';
import { Button, ButtonSave } from 'components/buttons';
import { Modal, ModalSize, NotificationToaster } from 'components/notifications';
import { useValidation } from 'hooks/useValidation';
import { FileExtractService } from 'services';

const defaultArray = [];

const badRequestCode = 400;
const requestDateFormat = "YYYY-MM-DD";

// How far back files are held, and so how far back the calendar goes.
const yearsOfHistory = 15;
const maxRangeYears = 1;

// The backend validates locations against the caller's Tacho permissions, so the picker matches.
const locationBusinessArea = "Tacho";

const validationMessages = {
    fileTypes: "Please select at least one file type.",
    startDate: "Please select a start date.",
    endDate: "Please select an end date.",
    rangeTooLong: `The date range cannot be longer than ${maxRangeYears} year.`,
    endBeforeStart: "The end date must be on or after the start date.",
    endInFuture: "The end date cannot be in the future.",
    locations: "Please select at least one location."
};

const generalErrorMessage = "Please try again.";

// The backend judges the upper bound against UTC today, so the picked date is read as a UTC day.
function isAfterUtcToday(date) {
    return moment.utc(moment(date).format(requestDateFormat)).isAfter(moment.utc().startOf('day'));
}

// The range control has one message slot, so both ends are reported together. The order and
// upper-bound checks are a backstop for what the server enforces; the control blocks both.
function getDateRangeMessage(startDate, endDate) {
    const messages = [];

    if (startDate == null) {
        messages.push(validationMessages.startDate);
    }

    if (endDate == null) {
        messages.push(validationMessages.endDate);
    } else if (startDate != null) {
        if (moment(endDate).isAfter(moment(startDate).add(maxRangeYears, 'year'), 'day')) {
            messages.push(validationMessages.rangeTooLong);
        } else if (moment(endDate).isBefore(moment(startDate), 'day')) {
            messages.push(validationMessages.endBeforeStart);
        } else if (isAfterUtcToday(endDate)) {
            messages.push(validationMessages.endInFuture);
        }
    }

    return messages.length === 0 ? null : messages.join(' ');
}

// Each rule spans more than one field, so none can be a field rule on the validation hook.
function getValidationRules({ selectedTypeIds, startDate, endDate, selectedLocations }) {
    const dateRangeMessage = getDateRangeMessage(startDate, endDate);

    return [
        {
            fieldName: "fileTypes",
            valid: selectedTypeIds.length > 0,
            errorMessage: validationMessages.fileTypes
        },
        {
            fieldName: "dateRange",
            valid: dateRangeMessage == null,
            errorMessage: dateRangeMessage
        },
        {
            fieldName: "locations",
            valid: selectedLocations.length > 0,
            errorMessage: validationMessages.locations
        }
    ];
}

// Each ticked type is requested from its own service, with each outcome reported separately.
export function FileExtractRequestModal(
    {
        showModal = false,
        onSave,
        onCloseModal,
        types = defaultArray
    }
) {

    const [selectedTypeIds, setSelectedTypeIds] = useState(defaultArray);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [selectedLocations, setSelectedLocations] = useState(defaultArray);
    const [useHierarchy, setUseHierarchy] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Seeded invalid so the button starts out of action, before the first validation pass runs.
    const [isValid, errors, validate] = useValidation(false);

    const fileTypeCheckboxItems = types.map((type) => ({
        id: type.id,
        name: type.name,
        checked: selectedTypeIds.includes(type.id),
        disabled: isSubmitting
    }));

    const earliestSelectableDate = moment().subtract(yearsOfHistory, 'years').toDate();
    const latestSelectableDate = moment().toDate();

    // Run on every change so the button and each field's message track the form as it stands.
    useEffect(runValidation, [selectedTypeIds, startDate, endDate, selectedLocations, showModal]);

    function runValidation() {
        if (!showModal) {
            return;
        }

        validate(defaultArray, null, getValidationRules({ selectedTypeIds, startDate, endDate, selectedLocations }));
    }

    // The checkbox list reports only the item that was toggled, so the selection is kept here.
    function onFileTypeChange(event, typeId) {
        setSelectedTypeIds((current) => {
            if (current.includes(typeId)) {
                return current.filter((id) => id !== typeId);
            }

            return [...current, typeId];
        });
    }

    function onDateRangeChange(range) {
        setStartDate(range.startDate.dateString);
        setEndDate(range.endDate.dateString);
    }

    function onSelectedLocationsChange(locations) {
        setSelectedLocations(locations);
    }

    function resetForm() {
        setSelectedTypeIds(defaultArray);
        setStartDate(null);
        setEndDate(null);
        setSelectedLocations(defaultArray);
    }

    function reportOutcome(results) {
        results.forEach((result) => {
            if (result.succeeded) {
                NotificationToaster.show(Intent.SUCCESS, `${result.fileType.name} extract requested.`);
                return;
            }

            let reason = generalErrorMessage;
            if (result.error?.status === badRequestCode) {
                reason = result.error.data;
            }

            NotificationToaster.show(Intent.DANGER, `${result.fileType.name} extract could not be requested. ${reason}`);
        });
    }

    function onSubmit() {
        if (!isValid || isSubmitting) {
            return;
        }

        setIsSubmitting(true);

        const selectedTypes = types.filter((type) => selectedTypeIds.includes(type.id));

        const requests = selectedTypes.map((selectedType) => {
            const request = {
                type: selectedType.id,
                startDate: startDate,
                endDate: endDate,
                locationIds: selectedLocations
            };

            return FileExtractService.requestExtract(selectedType.microserviceKey, request).then(
                () => ({ fileType: selectedType, succeeded: true }),
                (error) => ({ fileType: selectedType, succeeded: false, error })
            );
        });

        Promise.all(requests).then((results) => {
            reportOutcome(results);
            setIsSubmitting(false);

            // Nothing was requested, so the modal stays open on what the user entered.
            if (!results.some((result) => result.succeeded)) {
                return;
            }

            resetForm();
            onSave();
        });
    }

    return (
        <Modal
            isOpen={showModal}
            onClose={onCloseModal}
            clickOutsideClose={true}
            title="New Extract"
            size={ModalSize.MEDIUM}
            updateStateOnClose={false}
            footer={(
                <div className="bp6-dialog-footer-actions">
                    <Button
                        id="file-extract-request-cancel"
                        text="Cancel"
                        intent="secondary"
                        onClick={onCloseModal}
                        disabled={isSubmitting}
                    />
                    <ButtonSave
                        id="file-extract-request"
                        text="Request extract"
                        icon="download"
                        onClick={onSubmit}
                        simpleDisabled={!isValid}
                        disabled={isSubmitting}
                    />
                </div>
            )}
        >
            <FormCheckboxList
                id="file-extract-types"
                headingText="File type"
                items={fileTypeCheckboxItems}
                onChange={onFileTypeChange}
                dangerHelperText={errors.fileTypes}
            />

            <FormDateRangeInput
                id="file-extract-date-range"
                headingText="Date range"
                startDate={startDate}
                endDate={endDate}
                onChange={onDateRangeChange}
                minDate={earliestSelectableDate}
                maxDate={latestSelectableDate}
                dangerHelperText={errors.dateRange}
                disabled={isSubmitting}
                large={true}
            />

            <FormLocationSelector
                headingText="Locations"
                businessArea={locationBusinessArea}
                selectedLocations={selectedLocations}
                setSelectedLocations={onSelectedLocationsChange}
                useHierarchy={useHierarchy}
                setUseHierarchy={setUseHierarchy}
                useLocationDefaults={false}
                locationDefaultsToTrue={false}
                dangerHelperText={errors.locations}
                disabled={isSubmitting}
            />
        </Modal>
    );
}

FileExtractRequestModal.propTypes = {
    showModal: PropTypes.bool,
    onSave: PropTypes.func.isRequired,
    onCloseModal: PropTypes.func.isRequired,
    types: PropTypes.array
};
